import axios from 'axios';
import { auth } from '@/config/firebase';
import config from '@/config/constants';

/**
 * Safely turn an Axios error into a single human-readable string.
 *
 * The Cloud Functions error envelope is { code: 'ERROR', errors: ... }
 * where `errors` can be:
 *   - an Array (some endpoints push string messages)
 *   - an Object shaped like { fieldName: [msg, ...] } (formatErrors())
 *   - rarely a string
 * Calling `.join()` on the object form throws and swallows the real
 * 400/409/500 message — exactly what we hit on the grant flow. This
 * helper normalizes all three shapes plus axios fallbacks.
 */
export function humanizeAxiosError(e) {
  if (!e) return 'Unknown error';
  const data = e.response && e.response.data;
  if (data && data.errors) {
    const errs = data.errors;
    if (Array.isArray(errs)) {
      return errs.filter(Boolean).map(String).join(', ');
    }
    if (typeof errs === 'string') return errs;
    if (typeof errs === 'object') {
      const parts = [];
      for (const k of Object.keys(errs)) {
        const v = errs[k];
        if (Array.isArray(v)) parts.push(`${k}: ${v.join(', ')}`);
        else parts.push(`${k}: ${v}`);
      }
      if (parts.length) return parts.join(' · ');
    }
  }
  if (data && data.message) return data.message;
  if (data && typeof data === 'string') return data;
  return e.message || 'Unknown error';
}

/**
 * Build auth headers for the super-user endpoints. The super-user dashboard
 * flow runs pre-Kiey-login — only Firebase phone auth has fired. Match the
 * precedent in app_version_management/index.vue and rely on the Firebase ID
 * token alone.
 */
async function authHeaders() {
  const headers = {};
  const firebaseUser = auth && auth.currentUser;
  if (firebaseUser) {
    headers['X-Firebase-Token'] = await firebaseUser.getIdToken();
  }
  return headers;
}

export async function listMarkets() {
  const headers = await authHeaders();
  const res = await axios.get(`${config.api_url}super_admin/markets`, { headers });
  return res.data.data.rows;
}

export async function getMarketDetail(tenantId) {
  const headers = await authHeaders();
  const res = await axios.get(`${config.api_url}super_admin/markets/${tenantId}`, { headers });
  return res.data.data;
}

export async function setMarketZips(tenantId, zips, reason, areas) {
  const headers = await authHeaders();
  const body = { zips, reason: reason || null };
  if (Array.isArray(areas)) body.areas = areas;
  const res = await axios.post(
    `${config.api_url}super_admin/markets/${tenantId}/zips`,
    body,
    { headers },
  );
  return res.data.data;
}

export async function setMarketPrice(tenantId, payload) {
  const headers = await authHeaders();
  const res = await axios.post(
    `${config.api_url}super_admin/markets/${tenantId}/price`,
    payload,
    { headers },
  );
  return res.data.data;
}

export async function setMarketBillingDate(tenantId, nextPaymentAt, reason) {
  const headers = await authHeaders();
  const res = await axios.post(
    `${config.api_url}super_admin/markets/${tenantId}/billing_date`,
    { next_payment_at: nextPaymentAt, reason: reason || null },
    { headers },
  );
  return res.data.data;
}

export async function setMarketCoupon(tenantId, payload) {
  // payload: { coupon_id, reason } to apply; { remove: true, reason } to clear
  const headers = await authHeaders();
  const res = await axios.post(
    `${config.api_url}super_admin/markets/${tenantId}/coupon`,
    payload,
    { headers },
  );
  return res.data.data;
}

export async function grantMarket(tenantId, zips, reason, areas) {
  const headers = await authHeaders();
  const body = { tenant_id: tenantId, zips: Array.from(zips || []), reason: reason || null };
  if (Array.isArray(areas)) body.areas = areas;
  const res = await axios.post(
    `${config.api_url}super_admin/markets/grant`,
    body,
    { headers },
  );
  return res.data.data;
}

export async function releaseMarket(tenantId, reason) {
  const headers = await authHeaders();
  const res = await axios.post(
    `${config.api_url}super_admin/markets/${tenantId}/release`,
    { reason: reason || null },
    { headers },
  );
  return res.data.data;
}

export async function updateTenantAddress(tenantId, address, reason) {
  const headers = await authHeaders();
  const res = await axios.put(
    `${config.api_url}super_admin/markets/${tenantId}/tenant`,
    { address, reason: reason || null },
    { headers },
  );
  return res.data.data;
}

export async function searchUsers(q) {
  const headers = await authHeaders();
  const res = await axios.get(`${config.api_url}super_admin/users/search`, {
    headers,
    params: { q },
  });
  return res.data.data.results;
}

/**
 * Check whether an address falls inside any owner's exclusive market.
 * `zip` (from Google Places postal_code) drives the yes/no; `lat`/`lng`
 * drive the "nearest owner + distance" when it's outside. Either may be
 * omitted but at least one of (zip) or (lat & lng) must be present.
 * Returns { in_exclusive, owner, nearest, checked_zip, has_point }.
 */
export async function checkAddress({ zip, lat, lng }) {
  const headers = await authHeaders();
  const params = {};
  if (zip) params.zip = zip;
  if (typeof lat === 'number' && Number.isFinite(lat)) params.lat = lat;
  if (typeof lng === 'number' && Number.isFinite(lng)) params.lng = lng;
  const res = await axios.get(`${config.api_url}super_admin/markets/check_address`, {
    headers,
    params,
  });
  return res.data.data;
}
